import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { DialogButton, DialogTitleClose } from 'components';
import { format } from 'date-fns';
import { EdiOrdersQuery, SurveyResultEmbeddedFragment } from 'types.d';

type EdiOrderSummary = NonNullable<
  NonNullable<EdiOrdersQuery['ediOrders']>['nodes']
>[number];

type InboundDoc = {
  _id: string;
  type: string;
  filename: string;
  linkedPo?: string | null;
  linkedPos?: Array<string | null> | null;
  parsedJson?: string | null;
  error?: string | null;
  createdAt?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  order: EdiOrderSummary | null;
  surveyResult?: SurveyResultEmbeddedFragment;
  surveyName?: string;
  docs: InboundDoc[];
  loadingDocs?: boolean;
};

type JsonRecord = { [key: string]: any };
type ScopedDocResult = {
  selected: JsonRecord | null;
  legacyFallback: boolean;
};

const normalizePo = (value?: string | null) =>
  (value || '')
    .toString()
    .trim()
    .toUpperCase();

const parseDocJson = (doc?: InboundDoc): JsonRecord | null => {
  if (!doc?.parsedJson) {
    return null;
  }
  try {
    const parsed = JSON.parse(doc.parsedJson);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed as JsonRecord;
  } catch (err) {
    return null;
  }
};

const getOrderScopedDoc = (
  parsed: JsonRecord | null,
  type: '810' | '855' | '856',
  currentPo?: string | null,
): ScopedDocResult => {
  if (!parsed) {
    return {
      selected: null,
      legacyFallback: false,
    };
  }

  const normalizedCurrentPo = normalizePo(currentPo);
  const findByPo = (items: JsonRecord[]) => {
    if (items.length === 0) {
      return null;
    }
    if (!normalizedCurrentPo) {
      return items[0] || null;
    }
    const byPo = items.find(item => {
      const itemPo =
        item?.poNumber ||
        item?.header?.poNumber ||
        item?.invoice?.poNumber ||
        item?.ack?.poNumber;
      return normalizePo(itemPo) === normalizedCurrentPo;
    });
    return byPo || null;
  };

  if (type === '810') {
    const invoices = Array.isArray(parsed.invoices)
      ? (parsed.invoices.filter(Boolean) as JsonRecord[])
      : null;
    if (invoices && invoices.length > 0) {
      return {
        selected: findByPo(invoices),
        legacyFallback: false,
      };
    }
    return {
      selected: (parsed.invoice as JsonRecord) || null,
      legacyFallback: true,
    };
  }

  if (type === '855') {
    const acks = Array.isArray(parsed.acks)
      ? (parsed.acks.filter(Boolean) as JsonRecord[])
      : null;
    if (acks && acks.length > 0) {
      return {
        selected: findByPo(acks),
        legacyFallback: false,
      };
    }
    return {
      selected: parsed,
      legacyFallback: true,
    };
  }

  const shipments = Array.isArray(parsed.shipments)
    ? (parsed.shipments.filter(Boolean) as JsonRecord[])
    : null;
  if (shipments && shipments.length > 0) {
    return {
      selected: findByPo(shipments),
      legacyFallback: false,
    };
  }
  return {
    selected: parsed,
    legacyFallback: true,
  };
};

const formatDateDisplay = (date?: string | null) =>
  (() => {
    if (!date) {
      return '—';
    }
    const trimmed = String(date).trim();
    if (!trimmed) {
      return '—';
    }
    let parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      const numericOnly = trimmed.replace(/[^0-9]/g, '');
      if (numericOnly.length === 8) {
        const year = Number(numericOnly.slice(0, 4));
        const month = Number(numericOnly.slice(4, 6)) - 1;
        const day = Number(numericOnly.slice(6, 8));
        parsed = new Date(year, month, day);
      } else if (numericOnly.length === 6) {
        const year = Number(numericOnly.slice(0, 2)) + 2000;
        const month = Number(numericOnly.slice(2, 4)) - 1;
        const day = Number(numericOnly.slice(4, 6));
        parsed = new Date(year, month, day);
      } else {
        return trimmed;
      }
    }
    if (Number.isNaN(parsed.getTime())) {
      return trimmed;
    }
    const hasTime = /[T\s]\d{2}:\d{2}/.test(trimmed) || trimmed.length > 8;
    return format(parsed, hasTime ? 'MM/dd/yyyy hh:mm a' : 'MM/dd/yyyy');
  })();

const InfoItem: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box mb={1}>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body1" color="textPrimary">
      {value ?? '—'}
    </Typography>
  </Box>
);

const OrderStatusDialog: React.FC<Props> = ({
  open,
  onClose,
  order,
  surveyResult,
  surveyName,
  docs = [],
  loadingDocs = false,
}) => {
  const orderLines = order?.lines || [];
  const surveyLabel = useMemo(
    () =>
      surveyResult?.surveyDetail?.name ||
      surveyName ||
      surveyResult?.surveyDetail?.originSurvey ||
      '—',
    [surveyName, surveyResult],
  );

  const updatedByName = useMemo(() => {
    if (!surveyResult?.updatedBy) {
      return undefined;
    }
    const { firstName, middleName, lastName } = surveyResult.updatedBy;
    return [firstName, middleName, lastName].filter(Boolean).join(' ');
  }, [surveyResult]);

  const shipToAddress = useMemo(() => {
    if (!order) {
      return '—';
    }
    const parts = [
      order.shipToAddress1,
      order.shipToCity,
      order.shipToState,
      order.shipToZip,
    ]
      .map(part => (part || '').toString().trim())
      .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  }, [order]);

  const shipToName = order?.shipToName || '—';
  const currentPo = order?.poNumber || null;

  const invoiceDoc = useMemo(
    () => docs.find(doc => doc.type === 'INVOICE_810'),
    [docs],
  );
  const shipmentDoc = useMemo(() => docs.find(doc => doc.type === 'ASN_856'), [
    docs,
  ]);
  const ackDoc = useMemo(() => docs.find(doc => doc.type === 'PO_ACK_855'), [
    docs,
  ]);

  const invoiceJson = useMemo(() => parseDocJson(invoiceDoc), [invoiceDoc]);
  const shipmentJson = useMemo(() => parseDocJson(shipmentDoc), [shipmentDoc]);
  const ackJson = useMemo(() => parseDocJson(ackDoc), [ackDoc]);

  const scopedInvoice = useMemo(
    () => getOrderScopedDoc(invoiceJson, '810', currentPo),
    [currentPo, invoiceJson],
  );
  const scopedShipment = useMemo(
    () => getOrderScopedDoc(shipmentJson, '856', currentPo),
    [currentPo, shipmentJson],
  );
  const scopedAck = useMemo(
    () => getOrderScopedDoc(ackJson, '855', currentPo),
    [ackJson, currentPo],
  );

  const tabs = useMemo(
    () =>
      [
        { key: 'order', label: 'Order Details' },
        invoiceDoc ? { key: 'invoice', label: 'Invoice' } : null,
        shipmentDoc ? { key: 'shipment', label: 'Shipment' } : null,
        ackDoc ? { key: 'ack', label: 'Acknowledgement' } : null,
      ].filter(Boolean) as { key: string; label: string }[],
    [ackDoc, invoiceDoc, shipmentDoc],
  );

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
    }
  }, [open, tabs.length]);

  const safeActiveTab =
    tabs.length === 0 ? 0 : Math.min(activeTab, tabs.length - 1);

  const renderDocMeta = (doc?: {
    filename: string;
    createdAt?: string | null;
    error?: string | null;
  }) => {
    if (!doc) {
      return null;
    }
    return (
      <Box mb={2}>
        <Typography variant="caption" color="textSecondary">
          File
        </Typography>
        <Typography variant="body1">{doc.filename}</Typography>
        <Typography variant="caption" color="textSecondary">
          Received
        </Typography>
        <Typography variant="body1">
          {formatDateDisplay(doc.createdAt)}
        </Typography>
        {doc.error && (
          <>
            <Typography variant="caption" color="textSecondary">
              Error
            </Typography>
            <Typography variant="body1" color="error">
              {doc.error}
            </Typography>
          </>
        )}
      </Box>
    );
  };

  const renderLegacyNotice = (show: boolean) => {
    if (!show) {
      return null;
    }
    return (
      <Typography variant="caption" color="textSecondary">
        Legacy document format (single-view fallback)
      </Typography>
    );
  };

  const renderInvoice = () => {
    if (!invoiceDoc) {
      return null;
    }
    if (!invoiceJson) {
      return (
        <Typography variant="body2" color="textSecondary">
          Unable to parse invoice details.
        </Typography>
      );
    }
    const invoice = scopedInvoice.selected || null;
    if (!invoice) {
      return (
        <Typography variant="body2" color="textSecondary">
          No invoice details found for PO {currentPo || '—'} in this document.
        </Typography>
      );
    }
    const totals = invoice.totals || {};
    const lines = invoice.lines || [];
    return (
      <Box display="grid" gridGap={24}>
        {renderDocMeta(invoiceDoc)}
        {renderLegacyNotice(scopedInvoice.legacyFallback)}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <InfoItem label="Invoice #" value={invoice.invoiceNumber || '—'} />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem
              label="Invoice Date"
              value={formatDateDisplay(invoice.invoiceDate)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem label="PO Number" value={invoice.poNumber || '—'} />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem
              label="PO Date"
              value={formatDateDisplay(invoice.poDate)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem
              label="Invoice Total"
              value={
                totals.tdsAmount != null
                  ? `$${Number(totals.tdsAmount).toFixed(2)}`
                  : '—'
              }
            />
          </Grid>
        </Grid>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Invoice Lines
          </Typography>
          {lines.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No line items reported on this invoice.
            </Typography>
          ) : (
            <Box display="grid" gridGap={12}>
              {lines.map((line: any, index: number) => {
                const description = line.descriptions?.[0]?.description || '—';
                const productIds =
                  line.productIds
                    ?.map((id: any) =>
                      `${id.qualifier || ''} ${id.id || ''}`.trim(),
                    )
                    .filter(Boolean)
                    .join(', ') || '—';
                return (
                  <Box
                    key={`${line.lineNumber || index}-invoice`}
                    border={1}
                    borderColor="grey.300"
                    borderRadius={8}
                    p={2}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={2}>
                        <InfoItem
                          label="Line #"
                          value={line.lineNumber || index + 1}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <InfoItem
                          label="Quantity"
                          value={
                            line.quantity != null
                              ? `${line.quantity} ${line.uom || ''}`.trim()
                              : '—'
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <InfoItem
                          label="Unit Price"
                          value={
                            line.unitPrice != null
                              ? `$${Number(line.unitPrice).toFixed(2)}`
                              : '—'
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <InfoItem label="Product IDs" value={productIds} />
                      </Grid>
                      <Grid item xs={12}>
                        <InfoItem label="Description" value={description} />
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderShipment = () => {
    if (!shipmentDoc) {
      return null;
    }
    if (!shipmentJson) {
      return (
        <Typography variant="body2" color="textSecondary">
          Unable to parse shipment details.
        </Typography>
      );
    }
    const shipment = scopedShipment.selected;
    if (!shipment) {
      return (
        <Typography variant="body2" color="textSecondary">
          No shipment details found for PO {currentPo || '—'} in this document.
        </Typography>
      );
    }
    const header = shipment.header || {};
    const transport = shipment.transport || {};
    const items = shipment.items || [];
    const trackingNumbers = (shipment.trackingNumbers || []).filter(Boolean);
    const fallbackTracking = shipment.trackingNumber
      ? [shipment.trackingNumber]
      : [];
    const allTrackingNumbers =
      trackingNumbers.length > 0 ? trackingNumbers : fallbackTracking;
    const renderTrackingLinks = () => {
      if (allTrackingNumbers.length === 0) {
        return '—';
      }
      return allTrackingNumbers.map((tracking: string, index: number) => {
        const trimmed = (tracking || '').trim();
        if (!trimmed) {
          return null;
        }
        const url = `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(
          trimmed,
        )}`;
        return (
          <React.Fragment key={`${trimmed}-${index}`}>
            <a href={url} target="_blank" rel="noopener noreferrer">
              {trimmed}
            </a>
            {index < allTrackingNumbers.length - 1 ? ', ' : ''}
          </React.Fragment>
        );
      });
    };
    return (
      <Box display="grid" gridGap={24}>
        {renderDocMeta(shipmentDoc)}
        {renderLegacyNotice(scopedShipment.legacyFallback)}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <InfoItem label="Shipment ID" value={header.shipmentId || '—'} />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem
              label="Shipment Date"
              value={formatDateDisplay(header.date)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem
              label="Carrier"
              value={
                transport.scac
                  ? `${transport.scac} (${transport.routing || '—'})`
                  : transport.routing || '—'
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem label="Status" value={transport.status || '—'} />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem label="Tracking" value={renderTrackingLinks()} />
          </Grid>
        </Grid>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Shipped Items
          </Typography>
          {items.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No shipment line items reported.
            </Typography>
          ) : (
            <Box display="grid" gridGap={12}>
              {items.map((item: any, index: number) => {
                const description = item.descriptions?.[0]?.description || '—';
                const productIds =
                  item.productIds
                    ?.map((id: any) =>
                      `${id.qualifier || ''} ${id.id || ''}`.trim(),
                    )
                    .filter(Boolean)
                    .join(', ') || '—';
                const quantityInfo = item.quantities?.[0];
                return (
                  <Box
                    key={`${item.lineNumber || index}-shipment`}
                    border={1}
                    borderColor="grey.300"
                    borderRadius={8}
                    p={2}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={2}>
                        <InfoItem
                          label="Line #"
                          value={item.lineNumber || index + 1}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <InfoItem
                          label="Quantity"
                          value={
                            quantityInfo?.shippedQty != null
                              ? `${
                                  quantityInfo.shippedQty
                                } ${quantityInfo.uom || ''}`.trim()
                              : '—'
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <InfoItem label="Product IDs" value={productIds} />
                      </Grid>
                      <Grid item xs={12}>
                        <InfoItem label="Description" value={description} />
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderAck = () => {
    if (!ackDoc) {
      return null;
    }
    if (!ackJson) {
      return (
        <Typography variant="body2" color="textSecondary">
          Unable to parse acknowledgement details.
        </Typography>
      );
    }
    const scopedAckPayload = scopedAck.selected;
    if (!scopedAckPayload) {
      return (
        <Typography variant="body2" color="textSecondary">
          No acknowledgement details found for PO {currentPo || '—'} in this
          document.
        </Typography>
      );
    }
    const ack = scopedAckPayload.ack || scopedAckPayload;
    const header = scopedAckPayload.header || ack.header || {};
    const lines = ack.lines || [];
    return (
      <Box display="grid" gridGap={24}>
        {renderDocMeta(ackDoc)}
        {renderLegacyNotice(scopedAck.legacyFallback)}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <InfoItem
              label="Ack Code"
              value={ack.headerCode || header.ackTypeCode || '—'}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem label="PO Number" value={header.poNumber || '—'} />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoItem label="Ack Date" value={formatDateDisplay(header.date)} />
          </Grid>
        </Grid>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Line Responses
          </Typography>
          {lines.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No acknowledgement lines reported.
            </Typography>
          ) : (
            <Box display="grid" gridGap={12}>
              {lines.map((line: any, index: number) => (
                <Box
                  key={`${line.code || 'line'}-${line.lineNumber ||
                    index}-${line.dateOrReason ||
                    line.extras?.join('-') ||
                    index}`}
                  border={1}
                  borderColor="grey.300"
                  borderRadius={8}
                  p={2}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={2}>
                      <InfoItem label="Response" value={line.code || '—'} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <InfoItem
                        label="Quantity"
                        value={
                          line.quantity != null
                            ? `${line.quantity} ${line.uom || ''}`.trim()
                            : '—'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <InfoItem
                        label="Reason/Date"
                        value={
                          line.dateOrReason || line.extras?.join(', ') || '—'
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderTabContent = (key: string) => {
    if (key === 'invoice') {
      return renderInvoice();
    }
    if (key === 'shipment') {
      return renderShipment();
    }
    if (key === 'ack') {
      return renderAck();
    }

    if (!order) {
      return (
        <Typography variant="body2" color="textSecondary">
          No order found for this survey result.
        </Typography>
      );
    }

    return (
      <Box display="grid" gridGap={24}>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Order Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <InfoItem label="PO Number" value={order.poNumber || '—'} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem label="Status" value={order.status || '—'} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem label="Filename" value={order.filename || '—'} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem
                label="Created"
                value={formatDateDisplay(order.createdAt)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem label="Sent" value={formatDateDisplay(order.sentAt)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem label="ISA Control" value={order.isaControl || '—'} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem label="GS Control" value={order.gsControl || '—'} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem label="ST Control" value={order.stControl || '—'} />
            </Grid>
            <Grid item xs={12} md={8}>
              <InfoItem
                label="Ship To"
                value={
                  <>
                    {shipToName}
                    <br />
                    {shipToAddress}
                  </>
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem
                label="Ship To Account"
                value={order.shipToAccount || '—'}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Survey Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <InfoItem label="Survey" value={surveyLabel} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem
                label="Version"
                value={surveyResult?.surveyDetail?.version || '—'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem
                label="Answered At"
                value={formatDateDisplay(surveyResult?.answerDateTime)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem
                label="Working Status"
                value={surveyResult?.workingStatus || '—'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem
                label="Updated"
                value={formatDateDisplay(surveyResult?.updatedAt)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem label="Updated By" value={updatedByName || '—'} />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Order Lines
          </Typography>
          {orderLines.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No line items available.
            </Typography>
          ) : (
            <Box display="grid" gridGap={12}>
              {orderLines.map((line, index) => (
                <Box
                  key={`${line?.supplierItem || 'line'}-${index}`}
                  border={1}
                  borderColor="grey.300"
                  borderRadius={8}
                  p={2}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <InfoItem
                        label="Supplier Item"
                        value={line?.supplierItem || '—'}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <InfoItem
                        label="Buyer Item"
                        value={line?.buyerItem || '—'}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <InfoItem label="UOM" value={line?.uom || '—'} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <InfoItem
                        label="Quantity"
                        value={
                          line?.requestedEach != null
                            ? String(line.requestedEach)
                            : '—'
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitleClose title="Order Status" onClose={onClose} />
      <DialogContent dividers>
        <Box display="grid" gridGap={16}>
          <Tabs
            value={safeActiveTab}
            onChange={(_, idx) => setActiveTab(idx)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map(tab => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
          {loadingDocs && tabs.length > 1 && (
            <Box display="flex" justifyContent="flex-start">
              <CircularProgress size={20} />
            </Box>
          )}
          {tabs.map((tab, index) => (
            <div key={tab.key} role="tabpanel" hidden={index !== safeActiveTab}>
              {index === safeActiveTab && (
                <Box mt={1}>{renderTabContent(tab.key)}</Box>
              )}
            </div>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <DialogButton isCancel onClickButton={onClose} />
      </DialogActions>
    </Dialog>
  );
};

export default OrderStatusDialog;
